import { useState, useEffect } from "react";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Filler,
} from "chart.js";
import { Line } from "react-chartjs-2";
import api from "../../api";
import ChartInfoPopup from "./ChartInfoPopup";

ChartJS.register(
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Filler
);

const UsageOverTimeChart = () => {
  const [selectedRange, setSelectedRange] = useState(7);
  const [chartData, setChartData] = useState({ labels: [], datasets: [] });
  const [hovered, setHovered] = useState(null);

  const [themeColors, setThemeColors] = useState({});

  // read css variables
  const loadThemeColors = () => {
    const styles = getComputedStyle(document.documentElement);
    return {
      text: styles.getPropertyValue("--dark-color").trim(),
      grid: styles.getPropertyValue("--border-color").trim(),
      bg: styles.getPropertyValue("--background-color").trim(),
    };
  };

  useEffect(() => {
    setThemeColors(loadThemeColors());

    const observer = new MutationObserver(() => {
      setThemeColors(loadThemeColors());
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const fetchUsageData = async () => {
      try {
        const token =
          localStorage.getItem("access_token") ||
          sessionStorage.getItem("access_token");
        const baseUrl = process.env.REACT_APP_API_URL;

        const res = await api.get(
          `${baseUrl}/chat/over-usage-time?days=${selectedRange}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const usage = res.data.usage;

        const labels = usage.map((item) =>
          new Date(item.date).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
          })
        );

        const dataPoints = usage.map((item) => item.count);

        setChartData({
          labels,
          datasets: [
            {
              label: "Usage",
              data: dataPoints,
              borderColor: "#000",
              borderWidth: 2,
              pointBackgroundColor: "#000",
              pointRadius: 6,
              pointHoverRadius: 7,
              fill: true,
              backgroundColor: "rgba(0, 0, 0, 0.05)",
              tension: 0,
              clip: false,
            },
          ],
        });
      } catch (error) {
        console.error("❌ Failed to fetch usage over time:", error);
      }
    };

    fetchUsageData();
  }, [selectedRange]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        left: 20,
        right: 20,
      },
    },
    scales: {
      x: {
        offset: true,
        grid: {
          color: "#f1f1f1",
        },
        ticks: {
          font: { size: 12 },
        },
        title: {
          display: true,
          text: "Time Period",
          color: themeColors.text,
          font: { size: 13, weight: "bold" },
          padding: { top: 10 },
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          color: "#e0e0e0",
        },
        ticks: {
          stepSize: 20,
          font: { size: 12 },
        },
        title: {
          display: true,
          text: "Numbers of Conversations",
          color: themeColors.text,
          font: { size: 13, weight: "bold" },
        },
      },
    },
    plugins: {
      legend: { display: false },
    },
  };

  return (
    <div className="chart-card p-3 shadow-sm rounded profile-i mb-4">
      <div className="d-flex justify-content-between align-items-center mb-5">
        {/* <h5 className="fw-semibold mb-0">
          <span className="border-start border-4 border-dark ps-2">
            Usage Over Time
          </span>
        </h5> */}
        <div className="d-flex align-items-center gap-2 mb-3">
          <h4 className="fw-bold mb-0">
            <span className="border-start border-4 border-dark ps-2 refresh-prompts">
              Usage Over Time
            </span>
          </h4>

          <div className="chart-info-anchor">
            <ChartInfoPopup text="This chart shows the number of conversations handled by the chatbot over selectable periods (7, 30, or 90 days), with the Y-axis showing conversation counts and the X-axis updating the date range based on the selected filter (7, 30, or 90 days). It helps you track daily usage trends, compare short- vs long-term activity, and identify spikes in engagement." />
          </div>
        </div>

        {/* <div className="btn-group gap-1">
          {[7, 30, 90].map((range) => {
            const isActive = selectedRange === range;
            const isHover = hovered === range;

            return (
              <button
                key={range}
                className="btn-sm"
                onClick={() => setSelectedRange(range)}
                onMouseEnter={() => setHovered(range)}
                onMouseLeave={() => setHovered(null)}
                style={{
                  minWidth: "60px",
                  transition: "0.2s ease",
                  border: "1px solid var(--border-color)",

    
                  background: isActive
                    ? "var(--dark-color)" // selected
                    : isHover
                    ? "var(--folder-hover-bg)" // hover
                    : "transparent", // normal

        
                  color: isActive
                    ? "var(--dark-color)" // selected text
                    : "var(--dark-color)", // normal text
                }}
              >
                {range} days
              </button>
            );
          })}
        </div> */}

        <div className="range-group">
          {[7, 30, 90].map((range) => (
            <button
              key={range}
              className={`range-btn ${selectedRange === range ? "active" : ""}`}
              onClick={() => setSelectedRange(range)}
              type="button"
            >
              {range} days
            </button>
          ))}
        </div>
      </div>

      <div style={{ height: "300px", width: "100%" }}>
        <Line data={chartData} options={options} />
      </div>
    </div>
  );
};

export default UsageOverTimeChart;
